import { useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Card, CardHeader, Modal, ModalFooter } from '@/components/common';
import { formatDate } from '@/utils';
// Imported by path rather than through services/index.ts, which is outside
// this task's file domain.
import {
  CHANNEL_KYC_DOCUMENT_TYPES,
  channelKycService,
  type ChannelKycDocument,
} from '@/services/kycDocuments';
import {
  useChannelKycDocuments,
  useDeleteChannelKycDocument,
  useUploadChannelKycDocument,
  useVerifyChannelKycDocument,
} from '@/hooks/useKycDocuments';

/**
 * A channel's KYC documents — the Broker Admin's side and the agent's.
 *
 * ONE component reading ONE store, rendered on two screens: the channel detail
 * page (`canVerify`) and the agent's own My Portal (read-only verdict). That is
 * the whole requirement — an upload on either screen shows up on the other, and
 * the verification status and remark the broker records are what the agent
 * reads back. Two components over two tables would have drifted.
 *
 * The panel is slot-based rather than a free list: the five document types are
 * fixed, so an EMPTY slot is information — it is the outstanding item, and a
 * plain list of what happens to be uploaded hides exactly that.
 */

const STATUS_VARIANT: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  verified: 'success',
  rejected: 'error',
};

function VerdictModal({
  document,
  approve,
  onClose,
  onConfirm,
  isLoading,
}: {
  document: ChannelKycDocument | null;
  approve: boolean;
  onClose: () => void;
  onConfirm: (remarks: string) => void;
  isLoading: boolean;
}) {
  const [remarks, setRemarks] = useState('');

  const close = () => {
    setRemarks('');
    onClose();
  };

  return (
    <Modal
      isOpen={!!document}
      onClose={close}
      title={approve ? 'Verify document' : 'Reject document'}
      size="sm"
    >
      <div>
        <label htmlFor="channel-kyc-remarks" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Remarks {approve ? '(optional)' : ''}
        </label>
        <textarea
          id="channel-kyc-remarks"
          name="channel-kyc-remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          placeholder={
            approve ? 'e.g. Matches the IRDAI record.' : 'Say what is wrong — the channel sees this.'
          }
        />
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          This remark appears on the channel&apos;s own My Portal screen.
        </p>
      </div>
      <ModalFooter>
        <Button variant="outline" onClick={close}>
          Cancel
        </Button>
        <Button
          variant={approve ? 'primary' : 'danger'}
          onClick={() => onConfirm(remarks.trim())}
          // A rejection with no reason gives the agent nothing to act on, and
          // the server refuses it — so do not offer it here either.
          disabled={isLoading || (!approve && !remarks.trim())}
        >
          {isLoading ? 'Saving…' : approve ? 'Verify' : 'Reject'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/** The slot list a panel renders. Defaults to the five identity documents;
 *  the compliance card passes `CHANNEL_TRAINING_DOCUMENT_TYPES` so the training
 *  certificate is uploaded and reviewed by this same component against this
 *  same store, rather than by a second uploader that would drift. */
type DocumentSlots = readonly { value: string; label: string; required: boolean }[];

function DocumentSlot({
  slot,
  document,
  channelId,
  canVerify,
  canUpload,
  onVerdict,
}: {
  slot: DocumentSlots[number];
  document: ChannelKycDocument | undefined;
  channelId: string;
  canVerify: boolean;
  canUpload: boolean;
  onVerdict: (document: ChannelKycDocument, approve: boolean) => void;
}) {
  const uploadMutation = useUploadChannelKycDocument(channelId);
  const deleteMutation = useDeleteChannelKycDocument(channelId);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Once the broker has ruled on a document, only the broker side may remove
  // it — the verdict is a record, and its subject must not delete it out from
  // under them. The server enforces the same rule.
  const canRemove = !!document && (canVerify || document.status === 'pending');

  // B13. Opening a document is now a request, not a link, so it has the three
  // states a request has. A failure must SAY so rather than doing nothing,
  // which reads as "there is no file".
  const [viewing, setViewing] = useState(false);
  const [viewError, setViewError] = useState('');

  const handleView = async (target: ChannelKycDocument) => {
    setViewing(true);
    setViewError('');
    try {
      await channelKycService.download(target);
    } catch {
      setViewError('That document could not be opened. Refresh and try again.');
    } finally {
      setViewing(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="rounded-lg bg-gray-100 p-2 dark:bg-gray-700">
            <DocumentTextIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {slot.label}
              {slot.required && <span className="ml-1 text-red-500 dark:text-red-400">*</span>}
            </p>
            {document ? (
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                {document.original_filename || document.name}
                {document.uploaded_by_name ? ` · uploaded by ${document.uploaded_by_name}` : ''}
                {document.created_at ? ` · ${formatDate(document.created_at)}` : ''}
              </p>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">Not uploaded</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {document && (
            <Badge variant={STATUS_VARIANT[document.status] ?? 'default'} size="sm">
              {document.status_display}
            </Badge>
          )}
          {/* B13 — a plain href would have to point at `/policy/media/`, which
              nginx serves with no session at all. The server no longer emits
              that URL; the bytes come through the authenticated, broker-scoped
              download route instead. */}
          {document?.download_url && (
            <button
              type="button"
              onClick={() => void handleView(document)}
              disabled={viewing}
              className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
            >
              {viewing ? 'Opening…' : 'View'}
            </button>
          )}

          {canUpload && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*,.pdf"
                aria-label={`Upload ${slot.label}`}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  uploadMutation.mutate(
                    { document_type: slot.value, file },
                    {
                      onSuccess: () => {
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      },
                    }
                  );
                }}
              />
              <Button
                size="sm"
                variant="outline"
                leftIcon={<ArrowUpTrayIcon className="h-4 w-4" />}
                disabled={uploadMutation.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadMutation.isPending ? 'Uploading…' : document ? 'Replace' : 'Upload'}
              </Button>
            </>
          )}

          {canVerify && document && (
            <>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<CheckCircleIcon className="h-4 w-4" />}
                onClick={() => onVerdict(document, true)}
              >
                Verify
              </Button>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<XCircleIcon className="h-4 w-4" />}
                onClick={() => onVerdict(document, false)}
              >
                Reject
              </Button>
            </>
          )}

          {canRemove && (
            <button
              type="button"
              title="Remove document"
              aria-label={`Remove ${slot.label}`}
              className="text-gray-500 hover:text-red-600 disabled:opacity-50 dark:text-gray-400 dark:hover:text-red-400"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(document!.id)}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {document?.remarks && (
        <p
          className={`mt-2 text-xs ${
            document.status === 'rejected' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'
          }`}
        >
          {document.remarks}
          {document.verified_by_name ? ` — ${document.verified_by_name}` : ''}
          {document.verified_at ? `, ${formatDate(document.verified_at)}` : ''}
        </p>
      )}
      {viewError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{viewError}</p>}
    </div>
  );
}

export function ChannelKycDocumentsPanel({
  channelId,
  canVerify,
  canUpload = true,
  title = 'KYC Documents',
  subtitle,
  slots = CHANNEL_KYC_DOCUMENT_TYPES,
  bare = false,
}: {
  channelId: string;
  /** Broker staff only. The server decides regardless; this stops buttons an
   *  agent could never use from being drawn for them. */
  canVerify: boolean;
  canUpload?: boolean;
  title?: string;
  subtitle?: string;
  /** Which slots to draw. See `DocumentSlots`. */
  slots?: DocumentSlots;
  /** Drop the surrounding Card so the panel can sit INSIDE another card — the
   *  compliance lifecycle needs the certificate upload beside the stage it
   *  unlocks, and a card within a card reads as two unrelated things. */
  bare?: boolean;
}) {
  const { data, isLoading, isError, error, refetch } = useChannelKycDocuments(channelId);
  const verdictMutation = useVerifyChannelKycDocument(channelId);
  const [verdictFor, setVerdictFor] = useState<{ document: ChannelKycDocument; approve: boolean } | null>(
    null
  );

  const documents = data?.results ?? [];
  // Newest per type wins: the list is ordered `-created_at`, so the first row
  // for a type is the current one and a replaced document falls behind it.
  const latestByType = new Map<string, ChannelKycDocument>();
  documents.forEach((document) => {
    if (!latestByType.has(document.document_type)) latestByType.set(document.document_type, document);
  });

  const outstanding = slots.filter((slot) => slot.required && !latestByType.has(slot.value));

  const body = (
    <>
      <CardHeader
        title={title}
        subtitle={
          subtitle ??
          (canVerify
            ? 'Upload, review and record a verification status with remarks. The channel sees the status and your remarks on their own portal.'
            : 'Upload your documents here. Your broker reviews them and their decision appears below.')
        }
      />

      {/* Loading, error and empty are THREE states. Rendering a failed request
          as "nothing uploaded" would tell an agent their documents are missing
          when nobody has actually looked. */}
      {isLoading ? (
        <p className="py-4 text-sm text-gray-500 dark:text-gray-400">Loading documents…</p>
      ) : isError ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:bg-red-900/20 dark:border-red-900">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">
              We could not load these documents — this is not the same as there being none.
            </p>
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {error instanceof Error ? error.message : 'The request failed.'}
            </p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : (
        <>
          {documents.length === 0 && (
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              No documents uploaded yet — the required slots are listed below.
            </p>
          )}
          <div className="space-y-3">
            {slots.map((slot) => (
              <DocumentSlot
                key={slot.value}
                slot={slot}
                document={latestByType.get(slot.value)}
                channelId={channelId}
                canVerify={canVerify}
                canUpload={canUpload}
                onVerdict={(document, approve) => setVerdictFor({ document, approve })}
              />
            ))}
          </div>
          {outstanding.length > 0 && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Still required: {outstanding.map((slot) => slot.label).join(', ')}.
            </p>
          )}
        </>
      )}

      <VerdictModal
        document={verdictFor?.document ?? null}
        approve={verdictFor?.approve ?? true}
        isLoading={verdictMutation.isPending}
        onClose={() => setVerdictFor(null)}
        onConfirm={(remarks) => {
          if (!verdictFor) return;
          verdictMutation.mutate(
            { documentId: verdictFor.document.id, remarks, approve: verdictFor.approve },
            { onSuccess: () => setVerdictFor(null) }
          );
        }}
      />
    </>
  );

  return bare ? body : <Card>{body}</Card>;
}
